import React from 'react';
import Immutable from 'immutable';
import { pageContext, get, sortArray } from 'ctm-util-functions';

import {
    AutoCompleteCombo,
    Badge,
    Checkbox,
    CustomFieldGroupLayout,
    DateInputSet,
    SimpleFlexbox,
    HelpIndicator,
    Input,
    Label,
    LoadingPage,
    FormattedInput,
    FormattedInputWithButtons,
    MultiFieldAddress,
    NextStepButton,
    Radio,
    RadioButtonContainer,
    RadioButtonGroup,
    Select,
    SingleDatePicker,
    SubmitButton,
    ValidationIndicator,
    ValidationMessage
} from 'ctm-components';

import Address from 'ctm-components/components/form/address/address';

/**
 * @desc Contains factory functions for each type of form element
 */
class FormElementFactory {

    /**
     * Sets up the form element factory lookup table
     */
    constructor() {
        this.factories = {
            address: this.createSimpleElement(Address),
            autoCompleteCombo: this.createSimpleElement(AutoCompleteCombo),
            badge: this.createSimpleElement(Badge),
            badgegroup: this.createRadioButtonContainer(Badge),
            checkbox: this.createSimpleElement(Checkbox),
            date: this.createSimpleElement(Input),
            dateInputSet: this.createSimpleElement(DateInputSet),
            singleDatePicker: this.createSimpleElement(SingleDatePicker),
            email: this.createSimpleElement(Input),
            helpIndicator: this.createHelpIndicator,
            label: this.createLabel,
            subLabel: this.createSubLabel,
            subLabelWithHTML: this.createSubLabelWithHTML,
            /* Use this if you need to define a vertical specific layout with CSS/markup/schema to inject into a form */
            layout: this.createComponentWithLayout,
            loadingPage: this.createSimpleElement(LoadingPage),
            formatted: this.createSimpleElement(FormattedInput),
            formattedWithButtons: this.createSimpleElement(FormattedInputWithButtons),
            multiFieldAddress: this.createSimpleElement(MultiFieldAddress),
            nextStepButton: this.createNextStepButton,
            number: this.createSimpleElement(Input),
            /* This is rendered by the layout factory as a single radio button */
            radio: this.createSimpleElement(Radio),
            /* This is a radio button group e.g. Yes/No fields */
            radiogroup: this.createRadioButtonContainer(RadioButtonGroup),
            select: this.createSimpleElement(Select),
            text: this.createSimpleElement(Input),
            validationMessage: this.createSimpleElement(ValidationMessage),
            validationIndicator: this.createValidationIndicator,
            submitButton: this.createSubmitButton
        };

        this.visualProps = {
            helpIndicator: { xs: 2, sm: 1, xsOrder: 2, alignSelfCenter: 'xs', alignSelfStart: 'sm' }
        };
    }

    /**
     * Create a form element, based on the element type in the passed in schema
     * @param {Object} schema         - The form element schema
     * @param {Object} rules          - The rules for the form element
     * @param {Boolean} touched       - A reference to the touch state value for the form element
     * @param {Function} dispatch     - The dispatch function for dispatching actions to redux
     * @param {String} value   - The current / initial value for the form element
     * @param {Object} selectedValues   - The values selected from state that were specified for this element
     * @param {String} elementType    - An optional elementType, to force a specific type of component to be built,
     * ignoring the elementType in the schema
     * @param {Object} extraProps     - extraProps you want to pass down to all sub components
     * @returns {Node}                 - The built form element
     */
    createElement = (schema, rules, touched, dispatch, value, selectedValues, elementType = null, extraProps) => {
        const type = elementType || schema.field.type;
        if (type.startsWith('component:')) {
            const componentKey = type.split('component:')[1];
            const componentBuilder = pageContext.getProps().builders.component;
            return componentBuilder(componentKey, schema);
        }
        const props = {
            schema,
            rules,
            touched,
            key: `${schema.identifier}_${type}`,
            dispatch,
            value,
            selectedValues,
            ...extraProps
        };
        return this.factories[type](props);
    };

    /**
     * Create an element that doesn't require any extra processing apart from passing in props
     * @param {Class} component - The component class to build
     * @returns {Function} - A function that can called to apply props to the component class and return a component
     * instance
     */
    createSimpleElement = (component) => {
        return (elementProps) => React.createElement(component, elementProps);
    };

    /**
     * Create a next step button that appears as a submit button
     * @param {Object} elementProps - The props for the specific field from schema
     * @returns {Node} - A nextStep / submit button
     */
    createNextStepButton = (elementProps) => {
        const {
            variant = 'full_width'
        } = elementProps;
        return (
            <NextStepButton
                {...elementProps}
                variant={variant}
            />
        );
    };

    /**
     * Create a generic submit button that doesn't require page schema to submit
     * @param {Object} elementProps - The props for the specific field from schema
     * @returns {Node} - A SubmitButton component
     */
    createSubmitButton = (elementProps) => {
        const { schema, stateProps } = elementProps;
        return (
            <SubmitButton {...schema} stateProps={stateProps} />
        );
    };

    /**
     * Creates a radio button container that accepts a component and passes props through.
     * @todo: this doesn't support set event callbacks because the children components are created before the parent component.
     * @param {Class} component - The component class to build
     * @returns {Function} - A function that can called to apply props to the component class and return a component
     * text
     */
    createRadioButtonContainer = (component) => {
        return (elementProps) => {
            const elements = [];
            const { values } = elementProps.schema.field;
            if (values) {
                // This component accepts an array of objects that are hardcoded, OR it can retrieve its values from state.
                const valueOptions = Array.isArray(values) ? values : this.getValuesFromState(
                    elementProps);

                if (values.labelType === 'detailedLabel') {
                    const hasValidDetailedLabels = this.checkForInvalidDetailedLabel(valueOptions);
                    for (const radioOption of valueOptions) {
                        elements.push(this.createDetailedLabel(hasValidDetailedLabels,
                            radioOption,
                            elementProps,
                            component));
                    }
                    return <RadioButtonContainer {...elementProps}>{elements}</RadioButtonContainer>;
                }

                for (const radioOption of valueOptions) {
                    const singularElementProps = {
                        ...elementProps,
                        optionProps: radioOption,
                        key: `${elementProps.schema.identifier}_${radioOption.value}`,
                        ref: `${elementProps.schema.identifier}`
                    };
                    elements.push(this.createSimpleElement(component)(singularElementProps));
                }
                return <RadioButtonContainer {...elementProps}>{elements}</RadioButtonContainer>;
            }
            return this.createSimpleElement(component)(elementProps);
        };
    };

    /**
     * This is a higher order function returns a function call back used to prepare all props for detailedLabel component,
     * @param {boolean} hasValidDetailedLabels indicate if the option list has at least one invalid detailedLabel value.
     * @param {List} radioOption Immutable List of series options.
     * @param {Object} elementProps The schema object
     * @param {Class} component - The component class to build
     * @returns {boolean} return true if all 'detailedLabel' are valid, otherwise return false.
     */
    createDetailedLabel = (hasValidDetailedLabels, radioOption, elementProps, component) => {
        const { detailedLabel, content, value } = radioOption;
        let newDetailedLabel = detailedLabel;
        let newContent = content;

        // if the option doesn't have a valid detailedLabel, use the first tag value in content as teh new detailed label,
        // also remove the first tag value from the content collection.
        if (!hasValidDetailedLabels && newContent) {
            newDetailedLabel += ` ${newContent.get(0)}`;
            newContent = newContent.shift();
        }
        const singularElementProps = {
            ...elementProps,
            optionProps: {
                ...radioOption,
                detailedLabel: newDetailedLabel,
                content: newContent
            },
            key: `${elementProps.schema.identifier}_${value}`,
            ref: `${elementProps.schema.identifier}`
        };
        return this.createSimpleElement(component)(singularElementProps);
    };

    /**
     * This function is to check if at least one option has its 'detailedLabel' value to be
     * an empty string, null or undefined.
     * @param {Object} valueOptions Immutable List of series options.
     * @returns {boolean} return true if all 'detailedLabel' are valid, otherwise return false.
     */
    checkForInvalidDetailedLabel = (valueOptions) => {
        for (const option of valueOptions) {
            if (!option.detailedLabel) {
                return false;
            }
        }
        return true;
    };

    /**
     * Retrieve values from an array on state to create a list of radio button options.
     * @param {Object} elementProps The schema object
     * @returns {Array} An array of radio options dynamically generated by schema
     */
    getValuesFromState = (elementProps) => {
        const options = [];
        const config = get(elementProps, 'schema.field.values');
        if (!config) {
            return options;
        }
        const {
            stateKey,
            limit,
            sort,
            labelKey,
            labelType = 'label',
            contentKey,
            valueKey,
            variant
        } = config;

        const state = pageContext.getProps().store.getState();
        let stateData = get(state, stateKey);
        if (!stateData) {
            return options;
        }

        // Sort a large object by a sorting criteria e.g. popularity for car
        if (sort) {
            for (const obj of sort) {
                const sortingConfig = Immutable.fromJS({
                    sortBy: obj.sortBy,
                    sortDir: obj.sortDir,
                    sortType: obj.sortType
                });
                stateData = Immutable.fromJS(sortArray(sortingConfig, stateData));
                if (limit) {
                    // Limit the length of the items returned
                    stateData = stateData.slice(0, limit);
                }
            }
        } else if (limit) {
            // Limit the length of the items returned
            stateData = stateData.slice(0, limit);
        }

        for (const opt of stateData) {
            const trimmedOption = {
                [labelType]: get(opt, labelKey),
                value: get(opt, valueKey),
                variant
            };

            if (contentKey) {
                trimmedOption.content = get(opt, contentKey);
            }

            options.push({
                ...trimmedOption
            });
        }
        return options;
    };

    /**
     * A very specific pattern that involves creating a potentially
     * vertical specific layout and CSS, importing it into this CustomFieldGroupLayout wrapper
     * along with individually rendered elements as "children"
     * @param {Object} elementProps - The props for the specific field from schema
     * @returns {Node} A contained group of possibly different field elements.
     */
    createComponentWithLayout = (elementProps) => {
        const elements = [];
        if (elementProps.schema.field.values) {
            for (const optionProps of elementProps.schema.field.values) {
                const key = `${elementProps.schema.identifier}_${optionProps.value}`;
                const singularElementProps = {
                    ...elementProps,
                    optionProps,
                    key
                };
                const element = this.factories[optionProps.type](singularElementProps);
                elements.push(
                    <CustomFieldGroupLayout {...singularElementProps}>{element}</CustomFieldGroupLayout>);
            }
            return (
                <SimpleFlexbox {...elementProps.schema.field.gridProps}>{[...elements]}</SimpleFlexbox>
            );
        }
        // todo: fix this so we can pass a random input in.
        throw Error('Not meant to be used this way as yet...!');
        // return this.createSimpleElement(elementProps.componentName)(elementProps);
    };

    /**
     * Creates an icon indicating that a short help text is available. Nothing
     * is created if there is no configured help text
     * @param {Object} elementProps - The props for the HelpIndicator component
     * @returns {Node|null} - JSX help indicator if help text is specified in the schema, otherwise null
     */
    createHelpIndicator = (elementProps) => {
        if (!elementProps.schema.helpText) {
            return null;
        }
        const identifier = elementProps.schema.identifier;

        return (
            <HelpIndicator
                key={`${identifier}.helpIndicator`}
                {...elementProps}
                contentProps={elementProps.schema.helpText}
            />
        );
    };

    /**
     * Creates a helper message indicating some validation details set in the schema
     * Nothing is created if there is no configured validation indicator details.
     * @param {Object} elementProps - The props for the validationIndicator component
     * @returns {Node|null} - JSX validationIndicator if details are specified in the schema, otherwise null
     */
    createValidationIndicator = (elementProps) => {
        if (!elementProps.schema.validationIndicator) {
            return null;
        }
        const identifier = elementProps.schema.identifier;

        return (
            <ValidationIndicator
                key={`${identifier}.validationIndicator`}
                {...elementProps}
                contentProps={elementProps.schema.validationIndicator}
            />
        );
    };

    /**
     * Creates a sub label that wraps the supplied form element
     * @param {Object} _elementProps - The props for the Label component
     * @returns {Array|null} - The label components. This can be an array currently as FieldRow is the only
     * one that uses it.
     */
    createSubLabel = (_elementProps) => {
        if (!_elementProps.schema.subLabel) {
            return null;
        }
        return this.createLabel(_elementProps, 'subLabel');
    };

    /**
     * Creates a sub label with HTML that wraps the supplied form element with HTML content
     * @param {Object} _elementProps - The props for the Label component
     * @returns {Array|null} - The label components. This can be an array currently as FieldRow is the only
     * one that uses it.
     */
    createSubLabelWithHTML = (_elementProps) => {
        if (!_elementProps.schema.subLabelWithHTML) {
            return null;
        }
        return this.createLabel(_elementProps, 'subLabelWithHTML');
    };

    /**
     * Creates a label that wraps the supplied form element
     * @param {Object} _elementProps - The props for the Label component
     * @param {String} labelKey - The label key within the schema
     * @returns {Array|null} - The label components. This can be an array currently as FieldRow is the only
     * one that uses it.
     */
    createLabel = (_elementProps, labelKey = 'label') => {
        if (!_elementProps.schema.label) {
            return null;
        }
        const { key, ...elementProps } = _elementProps;
        const identifier = elementProps.schema.identifier;
        const helpIndicator = this.createHelpIndicator(elementProps);
        return <Label key={`${identifier}.${labelKey}`} labelKey={labelKey} helpIndicator={helpIndicator} {...elementProps} />;
    };

}

export const formElementFactory = new FormElementFactory();
